<script setup lang="ts">
import usePartner from '@/composables/partner'

import TabletCard from './TabletCard.vue'
import TabletIdentifierSection from './TabletIdentifierSection.vue'
import TabletPartnerSelector from './TabletPartnerSelector.vue'
import TabletConfigurationSuccess from './TabletConfigurationSuccess.vue'
import DeviceAssociation from '@/views/loyaltySupports/components/DeviceAssociation.vue'

import type { Partner, PartnerParameter, Tablet, TabletIdentifiers } from '@/types/types'
import axios from 'axios'

const props = defineProps<{
  tablets: Tablet[] | null
  tabletIdentifiers?: TabletIdentifiers
}>()

const emit = defineEmits(['onFetchData', 'onRegenerateCodeRequest'])

const { t } = useI18n()

const NO_INTEGRATION = '__none__'
const ANY = '__all__'

const associationFilter = ref('all')
const integrationFilter = ref(ANY)
const sortBy = ref('recent')

const integrationOptions = computed(() => {
  const partners = new Map<string, string>()
  let hasNone = false

  for (const tablet of props.tablets ?? []) {
    if (tablet.partnerDevice) {
      partners.set(tablet.partnerDevice.partner.slug, tablet.partnerDevice.partner.name)
    } else {
      hasNone = true
    }
  }

  const options = [...partners.entries()].map(([value, label]) => ({ value, label }))
  options.sort((a, b) => a.label.localeCompare(b.label))

  if (hasNone) {
    options.push({ value: NO_INTEGRATION, label: t('pages.marketplace.tabletsPage.filters.noIntegration') })
  }

  return options
})

watch(integrationOptions, (options) => {
  if (integrationFilter.value !== ANY && !options.some((option) => option.value === integrationFilter.value)) {
    integrationFilter.value = ANY
  }
})

const isFiltered = computed(
  () => associationFilter.value !== 'all' || integrationFilter.value !== ANY || sortBy.value !== 'recent'
)

const resetFilters = () => {
  associationFilter.value = 'all'
  integrationFilter.value = ANY
  sortBy.value = 'recent'
}

const connectedAt = (tablet: Tablet) => {
  const date = tablet.lastPingAt ?? tablet.lastLoginAt
  return date ? new Date(date).getTime() : 0
}

const visibleTablets = computed(() => {
  let list = [...(props.tablets ?? [])]

  if (associationFilter.value === 'linked') {
    list = list.filter((tablet) => !!tablet.partnerDevice)
  } else if (associationFilter.value === 'unlinked') {
    list = list.filter((tablet) => !tablet.partnerDevice)
  }

  if (integrationFilter.value === NO_INTEGRATION) {
    list = list.filter((tablet) => !tablet.partnerDevice)
  } else if (integrationFilter.value !== ANY) {
    list = list.filter((tablet) => tablet.partnerDevice?.partner.slug === integrationFilter.value)
  }

  const direction = sortBy.value === 'recent' ? -1 : 1
  return list.sort((a, b) => direction * (connectedAt(a) - connectedAt(b)) || a.id - b.id)
})
const notification = useNotification()
const { getTabletDetail, deleteTablet, tabletEmptyState } = useTablet()
const { getPartnerParameters } = usePartner()

const tablet = ref<{ integrate: () => void; selectedDevicePartnerDeviceId: number } | null>(null)
const tabletsBeingConfigured = ref<number[]>([])
const isTabletConfigurationModalOpen = ref(false)
const selectedTablet = ref<Tablet | null>(null)
const storeId = ref(null)
const tabletDetail = ref(null)
const partnerParameter = ref(null)
const isConfigurationModalLoading = ref(false)
const isConfigured = ref(false)
const isDeleteModalOpen = ref(false)
const isDeleting = ref(false)

const associablePartnerParameters = ref<PartnerParameter[]>([])

const canAssociatePartnerDevice = computed(() => {
  return (
    !partnerParameter.value &&
    selectedTablet.value?.canAssociatePartnerDevice &&
    associablePartnerParameters.value?.length
  )
})

const partnerToConfigure = (id) => {
  partnerParameter.value = associablePartnerParameters.value.find((p) => p.id === id)
  isConfigurationModalLoading.value = false
}

const handleDeviceAssociated = async () => {
  emit('onFetchData')
  isConfigured.value = true
}

const closeModale = () => {
  isConfigured.value = false
  isTabletConfigurationModalOpen.value = false
}

const getAssociablePartnerParameters = async (storeId = null) => {
  const partnerParameters = await getPartnerParameters({ isActive: true, storeId: storeId })
  isConfigurationModalLoading.value = false
  const filteredData = partnerParameters.filter((i) => i.partner.type === 'pos' || i.partner.slug === 'shopify')
  return filteredData
}

const handleConfigure = async (tablet: Tablet) => {
  isConfigurationModalLoading.value = true
  storeId.value = tablet.storeId
  tabletsBeingConfigured.value.push(tablet.id)
  partnerParameter.value = null
  tabletDetail.value = await getTabletDetail(tablet.id)
  associablePartnerParameters.value = await getAssociablePartnerParameters(tablet.storeId)
  isTabletConfigurationModalOpen.value = true
  selectedTablet.value = tablet
  tabletsBeingConfigured.value = tabletsBeingConfigured.value.filter((id) => id !== id)
}

const redirect = async (type: string) => {
  if (type === 'primaryButton') {
    window.open(
      'https://heypongohelp.zendesk.com/hc/fr/articles/21925971964305-Utiliser-l-application-tablette-en-magasin',
      '_blank'
    )
  } else {
    tabletEmptyState.value.refreshButton.isLoading = true
    await handleRefresh()
    tabletEmptyState.value.refreshButton.isLoading = false
  }
}

const isLoadingConfiguration = (tabletId: number) => {
  return tabletsBeingConfigured.value.includes(tabletId)
}

const openDeleteModal = (tablet: Tablet) => {
  selectedTablet.value = tablet
  isDeleteModalOpen.value = true
}

const closeDeleteModal = () => {
  selectedTablet.value = null
  isDeleteModalOpen.value = false
}

const deleteTabletAndPartnerIntegration = async () => {
  if (isDeleting.value === true) {
    return
  }
  isDeleting.value = true

  try {
    await deleteTablet(selectedTablet?.value?.id)
    notification.create('success', {
      text: t('pages.marketplace.tabletsPage.delete.notificationSuccess'),
      timeout: 3000
    })
  } catch {
    notification.create('error', {
      text: t('pages.marketplace.tabletsPage.delete.notificationError'),
      timeout: 3000
    })
  } finally {
    isDeleting.value = false
    isDeleteModalOpen.value = false
    emit('onFetchData')
  }
}

const handleRefresh = async () => {
  tabletEmptyState.value.refreshButton.isLoading = true
  emit('onFetchData')
  notification.create('success', {
    text: t('pages.emptyState.refreshNotification'),
    timeout: 2000
  })
  tabletEmptyState.value.refreshButton.isLoading = false
}

const handleIntegrate = () => {
  ;(tablet.value as any).integrate()
}

const currentPartnerAssociatedToTablet = computed<Partner | null>(() => {
  const tabletIntegration = selectedTablet.value.partnerDevice
  if (tabletIntegration?.isActive || tabletIntegration?.partner?.slug === 'shopify') {
    return tabletIntegration.partner
  }

  return null
})

const editTabletModalTitle = computed(() => {
  return `${t('pages.marketplace.tabletsPage.partnerSelector.title')} #${selectedTablet?.value?.id}`
})

const deleteModalContent = computed(() => ({
  title: t('pages.marketplace.tabletsPage.delete.title', { id: selectedTablet?.value?.id }),
  subtitle: t('pages.marketplace.tabletsPage.delete.subtitle'),
  linkText: t('global.words.delete'),
  cancelText: t('global.words.cancel'),
  icon: 'HeroTrashIconOutline',
  bgColor: 'red'
}))

const handleConsultationModeChange = async (isCustomerProfileConsultationMode: boolean) => {
  await axios.patch(`/tablets/${selectedTablet.value.id}`, {
    isCustomerProfileConsultationMode: isCustomerProfileConsultationMode
  })

  selectedTablet.value.isCustomerProfileConsultationMode = isCustomerProfileConsultationMode
}
</script>

<template>
  <div class="divide-border pb-6 sm:pb-12">
    <TabletIdentifierSection
      :tablet-identifiers="tabletIdentifiers"
      @on-regenerate-code-request="$emit('onRegenerateCodeRequest')"
    />
    <template v-if="tablets">
      <div class="mb-6 flex flex-wrap items-center gap-3">
        <slot name="filters" />

        <Select v-if="tablets.length > 0" v-model="associationFilter">
          <SelectTrigger class="w-auto min-w-40 bg-white">
            <SelectValue :placeholder="t('pages.marketplace.tabletsPage.filters.association')" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{{ t('pages.marketplace.tabletsPage.filters.all') }}</SelectItem>
            <SelectItem value="linked">{{ t('pages.marketplace.tabletsPage.filters.linked') }}</SelectItem>
            <SelectItem value="unlinked">{{ t('pages.marketplace.tabletsPage.filters.unlinked') }}</SelectItem>
          </SelectContent>
        </Select>

        <Select v-if="integrationOptions.length > 1" v-model="integrationFilter">
          <SelectTrigger class="w-auto min-w-40 bg-white">
            <SelectValue :placeholder="t('pages.marketplace.tabletsPage.filters.integration')" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">
              {{ t('pages.marketplace.tabletsPage.filters.allIntegrations') }}
            </SelectItem>
            <SelectItem v-for="option in integrationOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </SelectItem>
          </SelectContent>
        </Select>

        <Select v-if="tablets.length > 0" v-model="sortBy">
          <SelectTrigger class="w-auto min-w-52 bg-white">
            <SelectValue :placeholder="t('pages.marketplace.tabletsPage.filters.sort')" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">{{ t('pages.marketplace.tabletsPage.filters.sortRecent') }}</SelectItem>
            <SelectItem value="oldest">{{ t('pages.marketplace.tabletsPage.filters.sortOldest') }}</SelectItem>
          </SelectContent>
        </Select>

        <Button v-if="isFiltered" variant="ghost" @click="resetFilters">
          {{ t('pages.marketplace.tabletsPage.filters.reset') }}
        </Button>
      </div>

      <p v-if="tablets.length > 0 && visibleTablets.length === 0" class="text-sm text-muted-foreground">
        {{ t('pages.marketplace.tabletsPage.filters.empty') }}
      </p>

      <div v-if="visibleTablets.length > 0" class="grid grid-cols-1 gap-6 lg:grid-cols-3 2xl:grid-cols-4">
        <TabletCard
          v-for="tablet in visibleTablets"
          :key="tablet.id"
          :tablet="tablet"
          :is-loading="isLoadingConfiguration(tablet.id)"
          @on-refresh="emit('onFetchData')"
          @on-configure="handleConfigure"
          @on-delete="openDeleteModal"
        />
      </div>
      <TheEmptyState
        v-if="tablets.length === 0"
        :title="t('pages.tablet.emptyState.title')"
        :description="t('pages.tablet.emptyState.description')"
        module="tablet"
        :primary-button="tabletEmptyState.noResultButton"
        :secondary-button="tabletEmptyState.refreshButton"
        @on-click="redirect"
      />
    </template>
    <AppSkeleton v-else type="section" section-height="h-96" />
  </div>
  <BaseModal
    :is-open="isTabletConfigurationModalOpen"
    :heading-title="editTabletModalTitle"
    :cancel="partnerParameter && !isConfigured && t('global.words.cancel')"
    :link-text="partnerParameter && !isConfigured && t('global.words.save')"
    :is-disabled="!tablet?.selectedDevicePartnerDeviceId"
    @close="closeModale"
    @click="handleIntegrate"
  >
    <div class="mb-4 border-b pb-4 text-left">
      <h3 class="text-sm font-medium">{{ t('pages.marketplace.tabletsPage.consultationModeTitle') }}</h3>
      <div class="flex gap-2">
        <p class="text-sm">{{ t('pages.marketplace.tabletsPage.consultationModeDisclaimer') }}</p>
        <BaseSwitch
          id="isCustomerProfileConsultationMode"
          :model-value="selectedTablet.isCustomerProfileConsultationMode"
          @update:model-value="handleConsultationModeChange"
        />
      </div>
    </div>
    <div v-if="canAssociatePartnerDevice">
      <TabletPartnerSelector
        :selected-partner="currentPartnerAssociatedToTablet"
        :partner-parameters="associablePartnerParameters"
        @on-partner-parameter-selected="partnerToConfigure"
      />
    </div>
    <div v-else-if="!isConfigured">
      <DeviceAssociation
        ref="tablet"
        :partner-parameter="partnerParameter"
        :store-id="storeId"
        device-type="tablet"
        :tablet="selectedTablet"
        @on-device-associated="handleDeviceAssociated"
      />
    </div>
    <TabletConfigurationSuccess
      v-if="isConfigured"
      :tablet="tabletDetail"
      :partner-parameter="partnerParameter"
      @on-button-click="closeModale"
    />
    <AppSkeleton v-if="isConfigurationModalLoading" type="list" />
  </BaseModal>
  <BaseModal
    :is-open="isDeleteModalOpen"
    :is-processing="isDeleting"
    :text-title="deleteModalContent.title"
    :text="deleteModalContent.subtitle"
    :icon="deleteModalContent.icon"
    :link-text="deleteModalContent.linkText"
    :icon-color="deleteModalContent.bgColor"
    :cancel="deleteModalContent.cancelText"
    @close="closeDeleteModal"
    @click="deleteTabletAndPartnerIntegration"
  />
</template>
